import { useCallback, useMemo, useState } from "react"

import { graphql } from "@/company_admin/gql/__generated__"
import type { BankAccountType } from "@/company_admin/gql/__generated__/graphql"
import type { AccountingGroupFormData } from "@/company_admin/utils/forms/accountingGroup"
import ModalDialog from "@/main/components/ui/ModalDialog/ModalDialog"
import { useCompanyAdminMutation } from "@/provider/apollo/Apollo"

import styles from "../AccountingGroupModal.module.scss"
import AccountingGroupForm from "../form/AccountingGroupForm"

const UpdateAccountingGroupDetail = graphql(/* GraphQL */ `
	mutation UpdateAccountingGroupDetail(
		$input: UpdateAccountingGroupDetailInput!
	) {
		updateAccountingGroupDetail(input: $input) {
			accountingGroup {
				...AccountingGroupFields
			}
		}
	}
`)

type AccountingGroupForEdit = {
	id: string
	name: string
	displayName: string
	bankCode: string
	bankBranchCode: string
	bankAccountType: BankAccountType
	bankAccountNumber: string
	bankAccountHolderName: string
	billingEmails: readonly string[]
}

type Props = {
	accountingGroup: AccountingGroupForEdit | null
	refetchDetail: () => void
	refetchGroups: () => void
	refetchSettlements: () => void
}

export const useEditAccountingGroupModal = ({
	accountingGroup,
	refetchDetail,
	refetchGroups,
	refetchSettlements,
}: Props) => {
	const [isOpen, setIsOpen] = useState(false)
	const [updateAccountingGroup, { loading }] = useCompanyAdminMutation(
		UpdateAccountingGroupDetail
	)

	const defaultValues = useMemo<
		Partial<AccountingGroupFormData> | undefined
	>(() => {
		if (!accountingGroup) return undefined
		return {
			name: accountingGroup.name,
			displayName: accountingGroup.displayName,
			bankCode: accountingGroup.bankCode,
			bankBranchCode: accountingGroup.bankBranchCode,
			bankAccountType: accountingGroup.bankAccountType,
			bankAccountNumber: accountingGroup.bankAccountNumber,
			bankAccountHolderName: accountingGroup.bankAccountHolderName,
			billingEmails: [...accountingGroup.billingEmails],
		}
	}, [accountingGroup])

	const handleSubmit = useCallback(
		async (data: AccountingGroupFormData) => {
			if (!accountingGroup) return
			await updateAccountingGroup({
				variables: {
					input: {
						accountingGroupId: accountingGroup.id,
						name: data.name,
						displayName: data.displayName,
						bankCode: data.bankCode,
						bankBranchCode: data.bankBranchCode,
						bankAccountType: data.bankAccountType!,
						bankAccountNumber: data.bankAccountNumber,
						bankAccountHolderName: data.bankAccountHolderName,
						billingEmails: data.billingEmails as string[],
					},
				},
			})
			refetchDetail()
			refetchGroups()
			refetchSettlements()
			setIsOpen(false)
		},
		[
			accountingGroup,
			updateAccountingGroup,
			refetchDetail,
			refetchGroups,
			refetchSettlements,
		]
	)

	const render = useCallback(() => {
		return (
			<ModalDialog isOpen={isOpen} onOpenChange={setIsOpen}>
				<div className={styles.modal_content}>
					<h2 className={styles.modal_title}>経理グループを編集</h2>
					<AccountingGroupForm
						defaultValues={defaultValues}
						onSubmit={handleSubmit}
						onCancel={() => setIsOpen(false)}
						submitLabel="保存"
						isSubmitting={loading}
					/>
				</div>
			</ModalDialog>
		)
	}, [isOpen, defaultValues, handleSubmit, loading])

	return {
		render,
		openModal: () => setIsOpen(true),
	}
}
